import { useState, useEffect } from 'react';
import { Modal } from '../common';
import { Button, Input } from '../ui';
import { Label } from '../ui/Label';

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

export default function AddUserModal({ isOpen, onClose, onAdd, loading = false, user = null, isEditing = false }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    address: '',
    phone_number: '',
    role: 'respondent',
    // password: '',
    username: '',
    statut: 'ACTIF'
  });

  const [errors, setErrors] = useState({});

  // Initialiser le formulaire avec les données de l'utilisateur si édition
  useEffect(() => {
    if (isEditing && user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        address: user.address || '',
        phone_number: user.phone_number || '',
        role: user.role || 'respondent',
        statut: user.statut || 'ACTIF',
        username: user.username || '',
        password: ''
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        address: '',
        phone_number: '',
        role: 'respondent',
        statut: 'ACTIF',
        username: '',
        password: ''
      });
    }
    setErrors({});
  }, [isOpen, isEditing, user]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Le prénom est obligatoire';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Le nom est obligatoire';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'L\'adresse est obligatoire';
    }
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Le téléphone est obligatoire';
    }

    // if (!isEditing && !formData.password) {
    //   newErrors.password = 'Le mot de passe est obligatoire';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onAdd(formData);
    }
  };

  const footer = (
    <>
      <Button
        variant="outline"
        onClick={onClose}
        disabled={loading}
      >
        Annuler
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={loading}
        className="gap-2"
      >
        {loading && <Spinner />}
        <span>{loading ? (isEditing ? 'Modification...' : 'Ajout en cours...') : (isEditing ? 'Modifier' : 'Ajouter')}</span>
      </Button>
    </>
  );
// formulaire d'ajout ou de modification d'utilisateur, avec validation et affichage des erreurs
// titre du formulaire au debut mais pas au milieu du formulaire, et les champs du formulaire sont pré-remplis avec les données de l'utilisateur si c'est une modification, sinon ils sont vides pour un nouvel utilisateur

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Modifier un gestionnaire' : 'Ajouter un gestionnaire'}
      size="md" 
      position="top"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl mx-auto">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">Prénom *</Label>
            <Input
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Jean"
              disabled={loading}
            />
            {errors.first_name && <p className="text-xs text-red-600 mt-1">{errors.first_name}</p>}
          </div>
          <div>
            <Label htmlFor="last_name">Nom *</Label>
            <Input
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Dupont"
              disabled={loading}
            />
            {errors.last_name && <p className="text-xs text-red-600 mt-1">{errors.last_name}</p>}
          </div>
        </div>

        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="jean@example.com"
            disabled={loading}
          />
          {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
        </div>

        <div>
          <Label htmlFor="address">Adresse *</Label>
          <Input
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Maristes, Dakar, Sénégal"
            disabled={loading}
          />
          {errors.address && <p className="text-xs text-red-600 mt-1">{errors.address}</p>}
        </div>

        <div>
          <Label htmlFor="phone_number">Téléphone *</Label>
          <Input
            id="phone_number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="75 689 09 00"
            disabled={loading}
          />
          {errors.phone_number && <p className="text-xs text-red-600 mt-1">{errors.phone_number}</p>}
        </div>

        <div>
          <Label htmlFor="statut">Statut *</Label>
          <select
            id="statut"
            name="statut"
            value={formData.statut}
            onChange={handleChange}
            disabled={loading}
            className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          >
            <option value="ACTIF">Actif</option>
            <option value="INACTIF">Inactif</option>
          </select>
        </div>

        {/* {!isEditing && (
          <div>
            <Label htmlFor="password">Mot de passe *</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              disabled={loading}
            />
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
          </div>
        )} */}
      </form>
    </Modal>
  );
}
